import { t, Trans } from "@lingui/macro";
import cx from "classnames";
import { ReactNode, useMemo, useState } from "react";

import {
  selectTradeboxExecutionPrice,
  selectTradeboxFees,
  selectTradeboxFromToken,
  selectTradeboxFromTokenAmount,
  selectTradeboxIncreasePositionAmounts,
  selectTradeboxMarkPrice,
  selectTradeboxNextPositionValues,
  selectTradeboxState,
  selectTradeboxToToken,
  selectTradeboxTradeFlags,
} from "context/SyntheticsStateContext/selectors/tradeboxSelectors";
import { useSelector } from "context/SyntheticsStateContext/utils";
import { getBreakevenPriceMove, getBreakevenPriceMovePercentage } from "domain/synthetics/trade/utils/executionPreview";
import { getIsHighPositionImpact } from "domain/synthetics/trade/utils/warnings";
import { formatAmountFree, formatDeltaUsd, formatPercentage, formatUsd, formatUsdPrice, PRECISION } from "lib/numbers";

import { SyntheticsInfoRow } from "components/Synthetics/SyntheticsInfoRow";
import TooltipWithPortal from "components/Tooltip/TooltipWithPortal";

const SUGGESTED_TARGET_IMPACT_PERCENTAGE = (PRECISION * 75n) / 10000n; // 0.75%
const SUGGESTED_SCALE_DENOMINATOR = 10000n;
const MIN_SUGGESTED_SCALE_BPS = 500n; // Keep suggestions at or above 5% of current size.

function PreviewTooltipLabel({ label, content }: { label: ReactNode; content: ReactNode }) {
  return (
    <TooltipWithPortal
      handle={<span className="border-b border-dashed border-gray-lightest">{label}</span>}
      content={<div className="text-13 leading-[1.45] text-white">{content}</div>}
      position="top-end"
      maxAllowedWidth={320}
    />
  );
}

export function PositionExecutionPreviewRows() {
  const [previewMode, setPreviewMode] = useState<"actual" | "suggested">("actual");
  const tradeFlags = useSelector(selectTradeboxTradeFlags);
  const { isIncrease, isLong, isSwap, isTwap } = tradeFlags;
  const fees = useSelector(selectTradeboxFees);
  const executionPrice = useSelector(selectTradeboxExecutionPrice);
  const increaseAmounts = useSelector(selectTradeboxIncreasePositionAmounts);
  const nextPositionValues = useSelector(selectTradeboxNextPositionValues);
  const markPrice = useSelector(selectTradeboxMarkPrice);
  const fromToken = useSelector(selectTradeboxFromToken);
  const fromTokenAmount = useSelector(selectTradeboxFromTokenAmount);
  const toToken = useSelector(selectTradeboxToToken);
  const { setFocusedInput, setFromTokenInputValue } = useSelector(selectTradeboxState);

  const priceImpact = fees?.positionPriceImpact;
  const isHighPositionImpact = getIsHighPositionImpact(priceImpact);
  const suggestedTrade = useMemo(() => {
    if (
      !isHighPositionImpact ||
      priceImpact?.precisePercentage === undefined ||
      priceImpact.precisePercentage >= 0n ||
      !fromToken ||
      fromTokenAmount <= 0n
    ) {
      return undefined;
    }

    const absoluteImpact = 0n - priceImpact.precisePercentage;
    if (absoluteImpact <= SUGGESTED_TARGET_IMPACT_PERCENTAGE) {
      return undefined;
    }

    const rawScaleBps = (SUGGESTED_TARGET_IMPACT_PERCENTAGE * SUGGESTED_SCALE_DENOMINATOR) / absoluteImpact;
    const scaleBps = rawScaleBps < MIN_SUGGESTED_SCALE_BPS ? MIN_SUGGESTED_SCALE_BPS : rawScaleBps;
    const suggestedCollateralAmount = (fromTokenAmount * scaleBps) / SUGGESTED_SCALE_DENOMINATOR;

    if (suggestedCollateralAmount <= 0n || suggestedCollateralAmount >= fromTokenAmount) {
      return undefined;
    }

    const suggestedSizeUsd = increaseAmounts?.sizeDeltaUsd
      ? (increaseAmounts.sizeDeltaUsd * scaleBps) / SUGGESTED_SCALE_DENOMINATOR
      : undefined;

    return {
      scaleBps,
      collateralAmount: suggestedCollateralAmount,
      inputValue: formatAmountFree(suggestedCollateralAmount, fromToken.decimals, fromToken.decimals),
      sizeUsd: suggestedSizeUsd,
      targetImpactPercentage: 0n - SUGGESTED_TARGET_IMPACT_PERCENTAGE,
    };
  }, [fromToken, fromTokenAmount, increaseAmounts?.sizeDeltaUsd, isHighPositionImpact, priceImpact?.precisePercentage]);

  if (!isIncrease || isSwap || isTwap) {
    return null;
  }

  const breakevenMove = getBreakevenPriceMove({
    markPrice,
    entryPrice: nextPositionValues?.nextEntryPrice,
    isLong,
  });
  const breakevenMovePercentage = getBreakevenPriceMovePercentage({ markPrice, breakevenMove });
  const showSuggestedTab = Boolean(suggestedTrade);
  const activePreviewMode = showSuggestedTab ? previewMode : "actual";

  const applySuggestedTrade = () => {
    if (!suggestedTrade) return;

    setFromTokenInputValue(suggestedTrade.inputValue);
    setFocusedInput("from");
    setPreviewMode("actual");
  };

  return (
    <div className="flex flex-col gap-8 rounded-4 border border-stroke-primary bg-gray-darker px-10 py-10">
      {showSuggestedTab && (
        <div className="TradeBox-preview-tabs" role="tablist" aria-label={t`Trade preview options`}>
          <button
            type="button"
            role="tab"
            aria-selected={activePreviewMode === "actual"}
            className={cx("TradeBox-preview-tab", { active: activePreviewMode === "actual" })}
            onClick={() => setPreviewMode("actual")}
          >
            <Trans>Actual</Trans>
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activePreviewMode === "suggested"}
            className={cx("TradeBox-preview-tab", { active: activePreviewMode === "suggested" })}
            onClick={() => setPreviewMode("suggested")}
          >
            <Trans>Suggested</Trans>
          </button>
        </div>
      )}

      {activePreviewMode === "actual" ? (
        <>
          <SyntheticsInfoRow
            label={
              <PreviewTooltipLabel
                label={t`Expected Execution Price`}
                content={
                  <Trans>
                    Estimated protocol execution price before slippage. Market long entries use the protocol ask/max
                    mark price, so this can be higher than the chart price; current position price impact is included.
                  </Trans>
                }
              />
            }
            value={formatUsdPrice(executionPrice ?? undefined, { visualMultiplier: toToken?.visualMultiplier }) ?? "-"}
          />
          <SyntheticsInfoRow
            label={
              <PreviewTooltipLabel
                label={t`Estimated Entry Price`}
                content={
                  <Trans>
                    Estimated average entry price after this order opens. For a new position it should be close to the
                    execution price; when increasing an existing position it becomes the blended entry price.
                  </Trans>
                }
              />
            }
            value={
              formatUsdPrice(nextPositionValues?.nextEntryPrice, { visualMultiplier: toToken?.visualMultiplier }) ?? "-"
            }
          />
          <SyntheticsInfoRow
            label={t`Price Impact`}
            isWarning={isHighPositionImpact}
            value={
              <>
                <span
                  className={cx({
                    "text-primary-alpha-darker": priceImpact?.deltaUsd !== undefined && priceImpact.deltaUsd > 0n,
                    "text-yellow-500": isHighPositionImpact,
                  })}
                >
                  {formatDeltaUsd(priceImpact?.deltaUsd) ?? "-"}
                </span>
                {priceImpact?.precisePercentage !== undefined && (
                  <span className="text-gray-lightest">
                    {" "}
                    (
                    {formatPercentage(priceImpact.precisePercentage, {
                      signed: true,
                      bps: false,
                      displayDecimals: 3,
                    })}
                    )
                  </span>
                )}
              </>
            }
          />
          <SyntheticsInfoRow
            label={t`Collateral After Fees`}
            value={formatUsd(nextPositionValues?.nextCollateralUsd) ?? "-"}
          />
          <SyntheticsInfoRow
            label={t`Move to Breakeven`}
            value={
              breakevenMove === undefined
                ? "-"
                : `${formatUsdPrice(breakevenMove, { visualMultiplier: toToken?.visualMultiplier }) ?? "-"} (${formatPercentage(
                    breakevenMovePercentage,
                    { bps: false, displayDecimals: 3, fallbackToZero: true }
                  )})`
            }
          />
        </>
      ) : (
        suggestedTrade && (
          <div className="TradeBox-suggested-preview">
            <div className="TradeBox-suggested-copy">
              <Trans>Reduce order size to target a lower price impact before submitting.</Trans>
            </div>
            <SyntheticsInfoRow
              label={t`Suggested Pay`}
              value={`${suggestedTrade.inputValue} ${fromToken?.symbol ?? ""}`}
            />
            <SyntheticsInfoRow label={t`Suggested Size`} value={formatUsd(suggestedTrade.sizeUsd) ?? "-"} />
            <SyntheticsInfoRow
              label={t`Target Price Impact`}
              value={
                <span className="text-yellow-500">
                  {formatPercentage(suggestedTrade.targetImpactPercentage, {
                    signed: true,
                    bps: false,
                    displayDecimals: 3,
                  })}
                </span>
              }
            />
            <button type="button" className="TradeBox-use-suggested" onClick={applySuggestedTrade}>
              <Trans>Use Suggested Trade</Trans>
            </button>
          </div>
        )
      )}
    </div>
  );
}
